import { createApi } from '@reduxjs/toolkit/query/react';
import { Contact, Relationship } from './models';
import { baseApi } from '@centerpiece/components';
import { transformRelationships } from 'utils/contacts.utils';

export const relationship = baseApi
  .enhanceEndpoints({ addTagTypes: ['Relationships', 'Relationship'] })
  .injectEndpoints({
    endpoints: builder => ({
      getRelationships: builder.query<Relationship[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'relationship',
          method: 'GET',
          params: params ? params : { rollup: 'all' },
        }),
        providesTags: (result, error, arg) => ['Relationships'],
        transformResponse: (data: Relationship[], meta, arg) => {
          return transformRelationships(data);
        },
      }),
      getRelationship: builder.query<Relationship, string>({
        query: id => ({
          url: `relationship/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'Relationship', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createRelationship: builder.mutation({
        query: request => ({
          url: 'relationship',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Relationships'],
        // transformResponse: (relationship: Relationship, meta, arg) => {
        //   return {
        //     ...relationship?.contact_r,
        //     name: `${relationship.contact_r?.firstname} ${relationship.contact_r?.lastname}`,
        //     ...relationship,
        //     relationship: relationship.id,
        //   };
        // },
      }),
      updateRelationship: builder.mutation<Relationship, Relationship>({
        query: request => ({
          url: `relationship/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => ['Relationships'],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            relationship.util.updateQueryData(
              'getRelationship',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(relationship.util.invalidateTags(['Relationships']))
             */
            dispatch(relationship.util.invalidateTags(['Relationships']));
          }
        },
      }),
      deleteRelationship: builder.mutation<Relationship, number>({
        query: id => ({
          url: `relationship/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Relationship' as const, arg }, 'Relationships']
            : ['Relationships'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetRelationshipsQuery,
  useGetRelationshipQuery,
  useCreateRelationshipMutation,
  useUpdateRelationshipMutation,
  useDeleteRelationshipMutation,
  useLazyGetRelationshipsQuery,
  usePrefetch: useRelationshipsPrefetch,
} = relationship;

export const subscribeRelationship =
  relationship.endpoints.getRelationships.useQueryState;
